// src/components/Card.jsx
// Pinch Point card — the atomic unit of the product.
// Props:
//   post           { id, description, category, severity, status, authorName,
//                    timestamp, upvotes, upvotedBy, locationName, anonymous,
//                    flagged, resolved, aiSuggestedCategory? }
//   variant        "default" | "compact" | "expanded" | "horizontal"
//   sevViz         "bar" | "block" | "badge" | "background"   (overrides parent data-sev-viz)
//   currentUid     uid string (drives upvote state if upvotedBy doesn't include)
//   isAdmin        boolean
//   onUpvote(post)
//   onSmartTask(post)
//   onUpdateStatus(postId, newStatus)
//   onReport(post)
//   onDelete(post)
//
// CLAUDE CODE: prepend `import React from "react";` etc, replace
// the `const PPIcons = window.PPIcons; ...` lines with real imports,
// and add `export default X;` at the bottom (replacing `window.X = X`).

import React, { useState } from "react";
import * as PPIcons from "./_icons";
import { CategoryChip, Chip, SeverityChip, StatusChip, StatusPipeline, formatRelative } from "./_primitives";

function Card({
  post,
  variant = "default",
  sevViz,                  // optional override
  currentUid,
  isAdmin = false,
  onUpvote, onSmartTask, onUpdateStatus, onReport, onDelete,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const sev = post.severity || "low";
  const sevCode = sev === "critical" ? "crit" : sev === "moderate" ? "mod" : "low";
  const status = post.resolved ? "resolved" : (post.status || "new");
  const voted = post.upvotedBy?.includes(currentUid);

  // Default: block left-edge (chosen). The .pp-card--railed class now applies
  // a solid left border colored by severity. Other viz styles override via
  // data-sev-viz set on the wrapper (or on <html> globally via Tweaks).
  const cls = [
    "pp-card",
    `pp-card--sev-${sevCode}`,
    `pp-card--${variant}`,
    post.resolved ? "pp-card--resolved" : "",
    // Add the railed class unless sevViz explicitly suppresses the edge.
    (sevViz === "badge" || sevViz === "background") ? "" : "pp-card--railed",
  ].filter(Boolean).join(" ");

  // For a non-default viz, set data-sev-viz on the card so the overrides apply
  // locally without needing a global parent attribute.
  const wrapperAttrs = sevViz ? { "data-sev-viz": sevViz } : {};

  return (
    <article className={cls} {...wrapperAttrs}>
      <header className="pp-card-top">
        <CategoryChip category={post.category || "Uncategorized"} />
        {(!sevViz || sevViz === "badge") && <SeverityChip severity={sev} />}
        {variant === "expanded" && <StatusChip status={status} />}
        {post.flagged && (
          <Chip tone="status-investigating" dot={false}>
            <PPIcons.IconFlag style={{ width: 11, height: 11 }} /> Flagged
          </Chip>
        )}
        {post.aiSuggestedCategory && variant === "expanded" && (
          <Chip tone="ai" dot={false}>
            <PPIcons.IconSparkle style={{ width: 11, height: 11 }} /> AI assisted
          </Chip>
        )}

        <span className="pp-card-top-right">
          {isAdmin && (
            <button
              className="pp-icon-btn"
              style={{ width: 26, height: 26 }}
              aria-label="Post actions"
              onClick={() => setMenuOpen(o => !o)}
            >
              <PPIcons.IconMore style={{ width: 14, height: 14 }} />
            </button>
          )}
        </span>
      </header>

      <p className="pp-card-desc">{post.description}</p>

      {variant === "expanded" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <StatusPipeline status={status} />
          <span style={{
            fontSize: 11, color: "var(--text-faint)", fontFamily: "var(--font-mono)"
          }}>
            {status.toUpperCase()}
          </span>
        </div>
      )}

      <footer className="pp-card-foot">
        {post.anonymous ? (
          <span className="pp-author" style={{ color: "var(--text-faint)" }}>Anonymous</span>
        ) : (
          <span className="pp-author">{post.authorName || "Member"}</span>
        )}
        <span className="pp-sep">·</span>
        <span>{formatRelative(post.timestamp)}</span>
        {post.locationName && (
          <>
            <span className="pp-sep">·</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <PPIcons.IconLocation style={{ width: 10, height: 10 }} />
              {post.locationName}
            </span>
          </>
        )}

        <button
          className="pp-card-upvote"
          aria-pressed={voted}
          onClick={() => onUpvote && onUpvote(post)}
          title={voted ? "Remove upvote" : "Upvote"}
        >
          <PPIcons.IconArrowUp /> {post.upvotes || 0}
        </button>
      </footer>

      {menuOpen && isAdmin && (
        <CardMenu
          post={post}
          onClose={() => setMenuOpen(false)}
          onSmartTask={onSmartTask}
          onUpdateStatus={onUpdateStatus}
          onReport={onReport}
          onDelete={onDelete}
        />
      )}
    </article>
  );
}

function CardMenu({ post, onClose, onSmartTask, onUpdateStatus, onReport, onDelete }) {
  const items = [
    onSmartTask && { label: "Generate smart task", icon: <PPIcons.IconSparkle />, onClick: () => onSmartTask(post) },
    onUpdateStatus && post.status !== "investigating" && !post.resolved && {
      label: "Mark investigating", icon: <PPIcons.IconClock />, onClick: () => onUpdateStatus(post.id, "investigating"),
    },
    onUpdateStatus && !post.resolved && {
      label: "Mark resolved", icon: <PPIcons.IconCheckCircle />, onClick: () => onUpdateStatus(post.id, "resolved"),
    },
    onUpdateStatus && !post.resolved && {
      label: "Dismiss", icon: <PPIcons.IconX />, onClick: () => onUpdateStatus(post.id, "dismissed"),
    },
    onReport && { label: "Report", icon: <PPIcons.IconFlag />, onClick: () => onReport(post) },
    onDelete && { label: "Delete", icon: <PPIcons.IconTrash />, danger: true, onClick: () => onDelete(post) },
  ].filter(Boolean);

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 5 }}
        onClick={onClose}
      />
      <div style={{
        position: "absolute", top: 36, right: 8, zIndex: 6,
        background: "var(--bg-elevated)", border: "1px solid var(--border)",
        borderRadius: "var(--radius)", boxShadow: "var(--shadow-pop)",
        padding: 4, minWidth: 180,
      }}>
        {items.map((it, i) => (
          <button
            key={i}
            onClick={() => { it.onClick(); onClose(); }}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 8,
              padding: "8px 10px", background: "transparent", border: 0,
              borderRadius: 6, cursor: "pointer", textAlign: "left",
              color: it.danger ? "var(--pp-crit)" : "var(--text)",
              fontSize: 13, fontWeight: 500,
            }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <span style={{ display: "inline-flex", width: 14 }}>{it.icon}</span>
            {it.label}
          </button>
        ))}
      </div>
    </>
  );
}

export default Card;
